/**
 * Mail-on-Demand (MOD) print-shop admin types.
 *
 * Mirrors the digital-services admin contract under `/v1/abo/mod/*`
 * (tickets queue, ticket detail, status transitions and pricing config).
 */

/**
 * The statuses the operator QUEUE can contain. `imported` is deliberately not
 * one of them — see `ArchivedTicketStatus`.
 */
export type TicketStatus =
  | ApprovalTicketStatus
  | ItpTicketStatus
  | 'paid'
  | 'accepted'
  | 'in_production'
  | 'shipped';

/**
 * The two lanes BEFORE Paid (book-order-approval ADR-001): a large book order
 * waits for the member to approve the final book before it reaches the print
 * shop. Book orders only; a mailing never enters them.
 *
 * They are queue statuses (the Books queue lists them with counts) but NOT
 * production statuses: no print action, no batch and no status correction is
 * offered on them. DS refuses all of those too; hiding them here is the UI
 * half of the same guard.
 */
export type ApprovalTicketStatus = 'awaiting_approval' | 'changes_requested';

/**
 * The lane BEFORE Paid of a book order whose plan includes an Instant Trust
 * Package (itp-mvp): the order waits until an operator uploads the ITP PDFs
 * and marks the ITP ready, which moves it to Paid and releases the printer.
 * Like the approval lanes it is a queue status, not a production status: no
 * print action and no status correction (DS answers `TICKET_STATUS_HELD`).
 */
export type ItpTicketStatus = 'itp_pending';

/**
 * A historical order copied in from the book builder's own records. It states
 * how the record came to exist and claims nothing about how the order was
 * fulfilled, because no system holds that.
 *
 * It never appears in the queue. It is reached by looking up the order number a
 * member quotes, and it must never be rendered as something an operator can
 * action.
 */
export type ArchivedTicketStatus = 'imported' | 'cancelled';

/** Any status a TICKET can carry, including the archive. */
export type AnyTicketStatus = TicketStatus | ArchivedTicketStatus;

/**
 * What produced the order, and therefore which tab it belongs to.
 *
 * `mod` is a member-composed mailing; `book` is a print order placed in the
 * book builder and ingested from `bba.books.order`.
 *
 * This is NOT derivable from `assetKind`: mailings already use the `book` asset
 * kind, because mailing a book to leads is the core MOD product. Anything that
 * separates the two tabs must read `origin`.
 *
 * Optional on the wire so a DS deploy predating the field does not blank the
 * queue — `originOf()` reads an absent value as `mod`.
 */
export type TicketOrigin = 'mod' | 'book';

/** The three print pieces of a book ticket, in the order an operator uses them. */
export type TicketFileKind = 'cover' | 'manuscript' | 'invoice';

/**
 * Why a file cannot be served right now.
 *
 * - `not-generated`: no object was ever recorded for this kind.
 * - `not-rendered-yet`: recorded, but the object does not exist yet. Ingestion
 *   happens immediately while the book builder renders asynchronously, so this
 *   is the NORMAL early state of a fresh order — not a failure.
 * - `no-longer-stored`: the same absence on an IMPORTED record. Nothing is
 *   coming: the object is gone (print files predate mid-2022).
 * - `lookup-failed`: storage could not answer. "We could not tell" must never
 *   be shown as "not there".
 */
export type TicketFileUnavailableReason =
  | 'not-generated'
  | 'not-rendered-yet'
  | 'no-longer-stored'
  | 'lookup-failed';

/**
 * Who produced the file the print shop downloads: the book builder / invoice
 * service (`generated`) or an ABO operator's upload (`operator`).
 */
export type TicketFileSource = 'generated' | 'operator';

/** Availability of one print file, resolved by DS when the ticket is opened. */
export interface TicketFileStatus {
  kind: TicketFileKind;
  available: boolean;
  reason?: TicketFileUnavailableReason;
  /** Absent on a DS older than abo-print-files-upload; read as `generated`. */
  source?: TicketFileSource;
  /** Operator files only: the e-mail of the operator who uploaded it. */
  uploadedBy?: string;
  /** Operator files only: ISO timestamp of the upload. */
  uploadedAt?: string;
  /** Operator files only: the name of the uploaded file. */
  fileName?: string;
  /** True when an operator file is current and the generated one was kept. */
  canRestore?: boolean;
}

/**
 * The refusal codes of the print-file replace / restore, flip book replace,
 * ship-to and invoice regenerate routes (DS `print-file-refusal.ts`, plus the operator-token
 * refusals of `abo-request-refusal.ts`), keyed to the status DS answers each
 * with.
 */
export const PRINT_FILE_REFUSAL_STATUS = {
  PRINT_FILE_ORDER_NOT_EDITABLE: 409,
  PRINT_FILE_NOT_BOOK: 409,
  PRINT_FILE_MISSING: 400,
  PRINT_FILE_TOO_LARGE: 413,
  PRINT_FILE_NOT_PDF: 415,
  PRINT_FILE_NO_BUCKET: 409,
  PRINT_FILE_NOTHING_TO_RESTORE: 409,
  // The flip book replace (abo-print-files-flipbook-sync).
  FLIPBOOK_NOT_FOUND: 409,
  FLIPBOOK_NOT_READY: 409,
  FLIPBOOK_REPLACE_FAILED: 502,
  FLIPBOOK_UNAVAILABLE: 503,
  FLIPBOOK_REPLACE_TIMEOUT: 504,
  ABO_OPERATOR_UNVERIFIED: 401,
  ABO_OPERATOR_UNAVAILABLE: 503,
} as const;

export type PrintFileRefusalCode = keyof typeof PRINT_FILE_REFUSAL_STATUS;

/**
 * A freshly minted signed URL (`.../files/:kind/download`).
 *
 * Short-lived and NEVER cached: it is requested per click, used immediately and
 * dropped. Holding one would leave the operator with a dead link and would keep
 * a bearer credential in browser memory longer than it needs to exist.
 */
export interface TicketFileDownload {
  url: string;
  expiresAt: string;
}

export type FlipBookSource = 'published' | 'preview';

export type LeadSiteSource = 'ghl' | 'pagestead';

/** afy-flipping's processing state of the member's flip book; `unknown` when unreadable. */
export type FlipBookStatus = 'processing' | 'ready' | 'failed' | 'unknown';

/**
 * DS's answer to a flip book replace when afy-flipping took the PDF but DS
 * could not read the ticket back. Still a success; the page refetches.
 */
export interface FlipBookReplaceAccepted {
  orderId: string;
  replaced: true;
}

/** The last flip book replace made from Print Shop. */
export interface FlipBookReplacement {
  replacedBy: string;
  /** ISO timestamp. */
  replacedAt: string;
}

/**
 * A book ticket's flip book and lead-capture site, resolved by DS when the
 * ticket is opened (order-flipbook-links ADR-004). Both are PUBLIC pages, so
 * they are plain links — unlike the print files, which are signed per click.
 */
export interface TicketBookLinks {
  /** Published digital book, else the DMP preview flip-book viewer (ADR-001). */
  flipBookUrl: string | null;
  flipBookSource: FlipBookSource | null;
  /** GHL book page (non-legacy), else Pagestead (ADR-003). */
  leadSiteUrl: string | null;
  leadSiteSource: LeadSiteSource | null;
  /** True when a consulted source failed (not 404): a null may be wrong. */
  lookupFailed: boolean;
  /*
   * The flip book replace (abo-print-files-flipbook-sync). All absent on a DS
   * older than it, which reads as "no flip book to replace".
   */
  /** The member's published afy-flipping book; absent = none (or unknown). */
  flipBookId?: string;
  /** Present only with `flipBookId`. */
  flipBookStatus?: FlipBookStatus;
  flipBookReplacement?: FlipBookReplacement;
  /** The cover or manuscript was replaced after the flip book last was. */
  flipBookOutOfDate?: boolean;
}

/** Where a book order's copies ship — one address, not a mailing list. */
export interface BookShipTo {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

/**
 * An admin's correction of a book order's ship-to (DS `PATCH .../ship-to`,
 * abo-print-files-upload). DS requires `regenerateInvoice` on every call.
 */
export interface UpdateShipToPayload {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  regenerateInvoice: boolean;
}

/**
 * The address is saved even when the invoice could not be regenerated:
 * `invoiceRegenerated` is then false and `invoiceError` says why.
 */
export interface UpdateShipToResult {
  ticket: TicketDetail;
  invoiceRegenerated: boolean;
  invoiceError?: string;
}

/** DS answers any invalid ship-to body with this code (status 400). */
export const SHIP_TO_INVALID = 'SHIP_TO_INVALID';

export type TicketAction = 'accept' | 'start_production' | 'ship';

export type AssetKind =
  | 'book'
  | 'tb_export'
  | 'cover_letter'
  | 'book_cover_letter_bundle';

/** Render state of a recipient's personalized cover-letter PDF (DS PR #2000). */
export type CoverLetterStatus = 'pending' | 'rendered' | 'failed';

/** A single row of the print-shop queue (`GET /v1/abo/mod/tickets`). */
export interface TicketSummary {
  orderId: string;
  member: {
    name: string;
    email: string;
  };
  assetTitle: string;
  // Asset type + whether a book carries a separate cover (cover + manuscript
  // print on two machines, task 41). Optional to tolerate deploy skew before
  // the DS queue-row projection lands.
  assetKind?: AssetKind;
  hasCover?: boolean;
  // Which tab the row belongs to. Optional for deploy skew; read through
  // `originOf()`, which treats absent as `mod`.
  origin?: TicketOrigin;
  /**
   * Present only on a book order whose plan includes an Instant Trust Package
   * (itp-mvp); `ready` once an operator marked it ready. Absent on every other
   * row and on a DS older than itp-mvp.
   */
  itp?: { included: true; ready: boolean };
  // For a book row this is COPIES to one address; for a mailing it is the
  // resolved recipient count. DS normalizes it, so the column needs no branch.
  quantity: number;
  /**
   * The zero-padded number a member quotes, generated by the book builder.
   * Book orders only — a mailing has none. The queue shows it in the Order
   * column (a mailing falls back to the short order id).
   */
  afyOrderNumber?: string;
  paidAt: string;
  ageDays: number;
  // Widened for the archive: the lookup returns imported records too, and
  // typing them as queue statuses would let `actionForStatus` be called on one.
  status: AnyTicketStatus;
  trackingRef?: string;
}

/** DS paginator envelope (`{ data, meta }`); `meta.currentPage` is 0-indexed. */
export interface PaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

/** Per-status queue totals for the tabs (`GET /v1/abo/mod/tickets/counts`). */
export interface TicketCounts {
  /**
   * The approval lanes (book-order-approval). Optional for deploy skew: a DS
   * that predates them returns neither, and the tabs then show no badge rather
   * than a wrong zero.
   */
  awaiting_approval?: number;
  changes_requested?: number;
  /** The ITP lane (itp-mvp). Optional for the same deploy-skew reason. */
  itp_pending?: number;
  paid: number;
  accepted: number;
  in_production: number;
  shipped: number;
  /**
   * The archive lanes (cycle 2). Optional because a DS deploy that predates
   * them returns neither — an older backend must not blank the badges.
   */
  cancelled?: number;
  imported?: number;
  total: number;
}

/** Address snapshot kept before the member's first correction (ADR-006). */
export interface RecipientAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

/** One recipient row of the operator detail (`.../recipients`). */
export interface TicketRecipient {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  mailable: boolean;
  selected: boolean;
  skipReason?: string;
  overridden: boolean;
  originalAddress?: RecipientAddress;
  // Per-recipient personalized cover letter (DS PR #2000/#2002). The PDF is
  // rendered asynchronously at PAID, so `coverLetterStatus` reflects that job
  // and `coverLetterPdfUrl` is present only once `rendered`.
  coverLetterPdfUrl?: string;
  coverLetterStatus?: CoverLetterStatus;
  // Render diagnostics (DS follow-up): while a failed render is still being
  // auto-retried `coverLetterRetrying` is true (not yet terminal); once
  // terminal, `coverLetterFailureReason` carries an operator-friendly reason
  // and `coverLetterError` the raw developer detail. All optional — an older DS
  // deploy omits them and the cell falls back to today's generic behavior.
  coverLetterFailureReason?: string;
  coverLetterError?: string;
  coverLetterRetrying?: boolean;
  coverLetterAttempts?: number;
}

/** One audit entry of a ticket's `events` history. */
export interface TicketEvent {
  /**
   * The status the order left. ABSENT when the order was CREATED in `to` rather
   * than transitioned into it — how every ingested book order arrives.
   */
  from?: string;
  to: string;
  actor: {
    type: string;
    id: string;
  };
  meta?: Record<string, unknown>;
  createdAt: string;
}

/** Per-reason breakdown of recipients skipped at payment time. */
export interface SkipReasons {
  missingAddress: number;
  incompleteAddress: number;
  duplicate: number;
}

/**
 * Where a book order stands with the member's approval
 * (book-order-approval, techspec §3.4).
 *
 * - `awaiting`: the approval e-mail went (or is going) out; no decision yet.
 * - `changes_requested`: the member asked for changes; support follows up.
 * - `approved`: approved by the member's link or by support on their behalf.
 * - `bypassed`: the approval switch was off when the order arrived, so it was
 *   released straight to Paid without asking.
 */
export type ApprovalState =
  | 'awaiting'
  | 'changes_requested'
  | 'approved'
  | 'bypassed';

export type ApprovalEmailKind = 'request' | 'reminder';

export type ApprovalEmailStatus = 'pending' | 'sent' | 'failed';

export type ApprovalPrinterStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'not_applicable'
  // The order is held for its Instant Trust Package (itp-mvp): approved or
  // released, but the printer is e-mailed only once the ITP is ready. Sent by
  // a DS with the ITP release visibility; an older DS never sends it.
  | 'held';

export type ApprovalActorType = 'member' | 'admin' | 'system';

/**
 * The approval record of a book ticket (`approval` on the ticket detail).
 * Present only on book tickets that carry one — every order at or above the
 * approval threshold since the feature shipped. Dates are ISO strings.
 */
export interface TicketApproval {
  state: ApprovalState;
  /** Bumped on every re-send or revision; older links stop working. */
  generation: number;
  minPrints: number | null;
  /** When approval was FIRST requested. */
  requestedAt: string;
  lastEmail: {
    kind: ApprovalEmailKind;
    status: ApprovalEmailStatus;
    at: string | null;
  };
  reminderSentAt: string | null;
  expiresAt: string;
  /** The link has expired. The order stays where it is; nothing is cancelled. */
  expired: boolean;
  /** The member's LATEST change request, if any. Free text from the member. */
  changes: { comment: string; requestedAt: string } | null;
  decidedAt: string | null;
  decidedBy: { type: ApprovalActorType; id: string } | null;
  /** The reason support gave when approving on the member's behalf. */
  approvalReason: string | null;
  /** The printer's "New Order" e-mail, which waits for the approval. */
  printer: { status: ApprovalPrinterStatus; at: string | null };
}

/**
 * What the approval card's headline says, picked from the record and the
 * ticket status. One per distinct situation an operator has to read.
 */
export type ApprovalHeadline =
  | 'awaiting'
  // The order was cancelled before the member decided.
  | 'closed'
  | 'awaiting_expired'
  | 'changes_requested'
  | 'approved_by_member'
  | 'approved_by_support'
  | 'approved'
  | 'bypassed';

/** The small round icon in front of a conversation line. */
export type ApprovalConversationIcon =
  | 'mail'
  | 'bell'
  | 'refresh'
  | 'edit'
  | 'clock'
  | 'check'
  | 'printer'
  | 'alert'
  | 'slash';

export type ApprovalConversationTone = 'neutral' | 'success' | 'danger';

/**
 * One line of the approval conversation, oldest first. Built only from
 * `ticket.approval` and `ticket.events`; `at` is null when the API does not
 * say when it happened (a failed delivery, for example).
 */
export type ApprovalConversationEntry =
  | {
      kind: 'system';
      id: string;
      text: string;
      at: string | null;
      icon: ApprovalConversationIcon;
      tone: ApprovalConversationTone;
    }
  | {
      /** The member's change request. Free text from the member. */
      kind: 'member_comment';
      id: string;
      author: string;
      label: string;
      at: string;
      comment: string;
    }
  | {
      /** Support's reason for approving on the member's behalf. */
      kind: 'support_reason';
      id: string;
      author: string;
      label: string;
      at: string | null;
      reason: string;
    }
  | {
      /** Something that has not happened yet, drawn with a dashed icon. */
      kind: 'pending';
      id: string;
      text: string;
    };

/** Body of `POST /v1/abo/mod/tickets/:orderId/approval/approve`. */
export interface ApproveTicketPayload {
  /** Why support is approving for the member (3–500 characters). */
  reason: string;
}

/**
 * The optional reason category of an operator cancel (DS `reasonCategory`).
 * ABO no longer sends one; it is read only to label records that have it.
 */
export type BookCancelReasonCategory =
  | 'test_order'
  | 'member_request'
  | 'duplicate'
  | 'wrong_files'
  | 'fraud'
  | 'other';

/**
 * What happens to the member's credits on an operator cancel:
 * - `none`: nothing is returned;
 * - `deferred`: returned later ("Return credits"), once the credits cutover
 *   makes the ledger authoritative and freezes HubSpot;
 * - `ledger`: returned now, as one ledger refund.
 */
export type BookCancelRefundMode = 'none' | 'deferred' | 'ledger';

/**
 * Where a ledger refund's amount comes from: the order's own ledger debit
 * (fixed), or an amount the operator reads from the HubSpot history (capped).
 */
export type BookRefundAmountSource = 'debit' | 'operator';

export type BookRefundFlag = 'double_charge_suspected';

export type BookRefundOutcome = 'refunded' | 'nothing_owed';

/** An actor on the cancellation record. */
export interface BookCancelActor {
  type: string;
  id: string;
}

/** The credits returned for an operator-cancelled ticket. Dates are ISO. */
export interface TicketCancellationRefund {
  outcome: BookRefundOutcome;
  amountSource: BookRefundAmountSource;
  amount: number;
  entryId: string | null;
  idempotencyKey: string;
  at: string;
  by: BookCancelActor;
  flags: string[];
  /** Set when the credits were returned later, through "Return credits". */
  settledBy: BookCancelActor | null;
  noHubspotRestoreConfirmed: boolean | null;
}

/** The `cancellation` block of an operator-cancelled book ticket. */
export interface TicketCancellation {
  at: string;
  by: BookCancelActor;
  /**
   * The live status the order was cancelled from: an approval lane, Waiting
   * for ITP, Paid, Accepted, In production or Shipped.
   */
  fromStatus: TicketStatus;
  /** `null` (or absent) when the cancel was sent without a category. */
  reasonCategory?: BookCancelReasonCategory | null;
  /** Operator-typed text. Rendered as text, never as markup. */
  reason: string;
  refundMode: BookCancelRefundMode;
  refund: TicketCancellationRefund | null;
}

/** One refund choice of the cancel dialog, or the settle choice. */
export interface BookRefundOption {
  mode: BookCancelRefundMode;
  amountSource?: BookRefundAmountSource;
  /** Present for a `debit` amount only; an `operator` amount is entered. */
  amount?: number;
  /** The most that can be returned: what the order charged. */
  cap: number;
  flags: BookRefundFlag[];
}

/** Why a ticket cannot be cancelled (or settled) right now. */
export type BookCancelBlockedReason =
  | 'BOOK_CANCEL_NOT_BOOK_ORDER'
  | 'BOOK_CANCEL_IMPORTED'
  | 'BOOK_CANCEL_STATUS'
  | 'BOOK_CANCEL_NOT_SETTLEABLE';

/** Why the order's credits cannot be returned at all. */
export type BookRefundBlockedReason = 'debit_owner_mismatch' | 'nothing_charged';

/**
 * `GET /v1/abo/mod/tickets/:orderId/cancel/preview`: the cancel plan of a
 * live book ticket (`kind: 'cancel'`), or the settle plan of an operator-cancelled
 * one (`kind: 'settle'`). Read-only; the action routes recompute it and answer
 * 409 `BOOK_CANCEL_PLAN_CHANGED` when it no longer matches what was confirmed.
 */
export interface BookCancelPreview {
  kind: 'cancel' | 'settle';
  eligible: boolean;
  blockedReason: BookCancelBlockedReason | null;
  orderId: string;
  status: AnyTicketStatus;
  afyOrderNumber: string | null;
  copies: number | null;
  memberEmail: string | null;
  placedAt: string | null;
  creditsChargedReported: number | null;
  ledgerDebit: {
    found: boolean;
    applied: number | null;
    createdAt: string | null;
    ownerMatches: boolean | null;
  };
  memberBackfillAt: string | null;
  ledgerOnlySince: string | null;
  ledgerAuthoritative: boolean;
  frozen: boolean;
  /**
   * Cancel: `none` plus at most one of `deferred` or `ledger` (only `none`
   * when `refundBlockedReason` is set). Settle: empty or one `ledger` option.
   */
  refundOptions: BookRefundOption[];
  refundBlockedReason: BookRefundBlockedReason | null;
  /**
   * The printer may already have the order. Information only: the dialog
   * shows one line, and nothing is asked of the operator.
   */
  printerAlreadyNotified: boolean;
}

/** Body of `POST /v1/abo/mod/tickets/:orderId/cancel`, as ABO sends it. */
export interface CancelTicketPayload {
  /** 3–500 characters once trimmed. */
  reason: string;
  refundMode: BookCancelRefundMode;
  /**
   * `ledger` only. ABO returns the order's own debit; an operator-typed
   * amount is engineering-only, through the API.
   */
  amountSource?: 'debit';
  /** `ledger` only: the debit option's amount. */
  amount?: number;
  /**
   * The preview's `printerAlreadyNotified`, sent silently (nothing is asked):
   * a DS from before cancel-from-any-status refuses a cancel without it, and
   * the current DS ignores it. Drop it once that DS is everywhere.
   */
  printerNoticeAcknowledged?: boolean;
}

/** Body of `POST /v1/abo/mod/tickets/:orderId/cancel/settle-refund`. */
export interface SettleTicketRefundPayload {
  /** ABO settles the order's own debit only. */
  amountSource: 'debit';
  amount: number;
  noHubspotRestoreConfirmed: true;
}

/** Where an order's Instant Trust Package stands (itp-mvp). */
export type TicketItpStatus = 'pending' | 'ready';

/** One ITP PDF on the ticket. Removed files are left out by DS. */
export interface TicketItpFile {
  id: string;
  fileName: string;
  /** Bytes. */
  size: number;
  /** The e-mail of the operator who added it. */
  uploadedBy: string;
  /** ISO timestamp. */
  uploadedAt: string;
}

/**
 * The `itp` block of a book ticket (itp-mvp). Present only when the order's
 * plan includes an Instant Trust Package: a set of printed pieces that ship in
 * the same box as the books. Dates are ISO strings.
 */
/**
 * Where the printer's New Order e-mail of an ITP order stands (itp-mvp):
 * `held` while the order waits for its ITP, then the release state.
 */
export type ItpPrinterStatus = 'held' | 'pending' | 'sent' | 'failed' | 'not_applicable';

export interface TicketItp {
  required: true;
  /** How many sets the plan includes. */
  sets: number;
  status: TicketItpStatus;
  files: TicketItpFile[];
  readyAt: string | null;
  readyBy: { type: string; id: string } | null;
  /**
   * The printer release of the order. Optional: a DS older than the ITP
   * release visibility does not send it, and the card then shows no line.
   */
  printer?: { status: ItpPrinterStatus; at: string | null };
}

/**
 * The refusal codes of the ITP routes (DS `itp-refusal.ts`), plus the
 * print-file codes the upload shares and the operator-token refusals, keyed to
 * the status DS answers each with.
 */
export const ITP_REFUSAL_STATUS = {
  PRINT_FILE_MISSING: 400,
  PRINT_FILE_NOT_PDF: 415,
  PRINT_FILE_TOO_LARGE: 413,
  ITP_NOT_REQUIRED: 409,
  ITP_NOT_EDITABLE: 409,
  ITP_NO_BUCKET: 409,
  ITP_NO_FILES: 409,
  ITP_FILE_NOT_FOUND: 404,
  ABO_OPERATOR_UNVERIFIED: 401,
  ABO_OPERATOR_UNAVAILABLE: 503,
} as const;

export type ItpRefusalCode = keyof typeof ITP_REFUSAL_STATUS;

/**
 * DS refuses a status correction of an order held for its ITP with this
 * code (409): correcting it would skip "Mark ITP ready" and the printer release.
 */
export const TICKET_STATUS_HELD = 'TICKET_STATUS_HELD';

/** Full ticket payload (`GET /v1/abo/mod/tickets/:orderId`). */
export interface TicketDetail {
  orderId: string;
  // DS guarantees a member (falls back to UNKNOWN_MEMBER), same as the queue
  // summary; still rendered defensively against malformed payloads.
  member: {
    name: string;
    email: string;
  };
  // Widened for the archive — see the queue row above. The compiler now forces
  // every consumer to decide what an imported ticket does.
  status: AnyTicketStatus;
  asset: {
    kind: AssetKind;
    title: string;
    // The print manuscript (book interior, or the single combined file).
    pdfUrl: string;
    // Book cover PDF (separate machine). Present only when the book has a true
    // interior manuscript; absent for exports / preview-only books (task 41).
    coverPdfUrl?: string;
    thumbnailUrl?: string;
  };
  // Present when the order includes a personalized cover letter (book add-on).
  // Each recipient then carries their own rendered letter (see TicketRecipient).
  coverLetter?: { templateKey: string };
  // All five below are present only on a book ticket (`origin === 'book'`).
  origin?: TicketOrigin;
  /** Copies to ONE address. Mirrors `quantity`, kept explicit for clarity. */
  copies?: number;
  /** The zero-padded number the member and the operator quote to each other. */
  afyOrderNumber?: string;
  shipTo?: BookShipTo;
  /**
   * True when the order arrived with no address. Rendered as an explicit
   * warning: a physical shipment must never go to an inferred address, and a
   * blank field would read as "nothing to see here".
   */
  shipToMissing?: boolean;
  /** Per-file availability, resolved by DS at open time. Empty for a mailing. */
  files?: TicketFileStatus[];
  /**
   * Book tickets whose print files an operator may replace or restore: a book
   * order, not imported, not shipped or cancelled. Absent on a DS older than
   * abo-print-files-upload, which reads as not editable.
   */
  editable?: boolean;
  /**
   * Book tickets only. Absent on a mailing, and on a book ticket served by a DS
   * older than order-flipbook-links — the rows then read "not available".
   */
  links?: TicketBookLinks;
  /**
   * Book tickets that went through (or skipped) member approval only. Absent
   * on every other ticket, and on a DS older than book-order-approval.
   */
  approval?: TicketApproval;
  /**
   * Book tickets whose plan includes an Instant Trust Package only
   * (itp-mvp). Absent on every other ticket, and on a DS older than itp-mvp.
   */
  itp?: TicketItp;
  /**
   * The member's DS id. Absent on an imported order whose member is gone, and
   * on a DS older than the operator cancel.
   */
  customerId?: string;
  /**
   * Book tickets an ABO operator cancelled only. Absent on every other
   * ticket, including cancelled imports and member (MOD) cancels.
   */
  cancellation?: TicketCancellation;
  quantity: number;
  skippedCount: number;
  skipReasons: SkipReasons;
  creditsCharged: number;
  paidAt: string;
  trackingRef?: string;
  statusTimestamps: Partial<Record<TicketStatus, string>>;
  events: TicketEvent[];
}

/** Body of `POST /v1/abo/mod/tickets/:orderId/status`. */
export interface UpdateTicketStatusPayload {
  action: TicketAction;
  trackingRef?: string;
}

/**
 * Statuses an operator may correct a ticket to (task 60). Mirrors the DS
 * `CORRECTABLE_STATUSES` lane (task 59) — the production lane of `TicketStatus`
 * (never the approval lanes or the ITP lane), kept as a runtime list so the picker can offer
 * it (minus the current status).
 */
export const CORRECTABLE_STATUSES: TicketStatus[] = [
  'paid',
  'accepted',
  'in_production',
  'shipped',
];

/** Body of `POST /v1/abo/mod/tickets/:orderId/status/correct` (task 60). */
export interface CorrectTicketStatusPayload {
  status: TicketStatus;
  reason?: string;
}

/** Queue status filter: a concrete status or every actionable ticket. */
/**
 * What the queue's status tabs can be set to.
 *
 * Widened for the archive: `cancelled` and `imported` are tabs now, so the
 * filter has to be able to name them. The DS side validates against its own
 * visible set, so an unknown value is rejected there rather than silently
 * falling back — which it used to do.
 */
export type TicketStatusFilter = AnyTicketStatus | 'all';

/**
 * The typed refusals of the cancel routes and the HTTP status DS answers each
 * with. The body is the DS error envelope plus `code` (`{statusCode, error,
 * errors: [text], code, ...}`, no `message`). A refusal is recognised only when the code is
 * known AND the status matches (`getBookCancelRefusal`).
 */
export const BOOK_CANCEL_REFUSAL_STATUS = {
  BOOK_CANCEL_OPERATOR_UNVERIFIED: 401,
  BOOK_CANCEL_FORBIDDEN: 403,
  BOOK_CANCEL_UNAVAILABLE: 503,
  BOOK_CANCEL_NOT_FOUND: 404,
  BOOK_CANCEL_NOT_BOOK_ORDER: 422,
  BOOK_CANCEL_IMPORTED: 422,
  BOOK_CANCEL_AMOUNT_OVER_CAP: 422,
  // A lost race only. (BOOK_CANCEL_STATUS_HISTORY is retired: DS no longer
  // answers it, since any live status can be cancelled.)
  BOOK_CANCEL_STATUS: 409,
  BOOK_CANCEL_ALREADY_CANCELLED: 409,
  BOOK_CANCEL_PLAN_CHANGED: 409,
  BOOK_CANCEL_NOT_SETTLEABLE: 409,
} as const;

export type BookCancelRefusalCode = keyof typeof BOOK_CANCEL_REFUSAL_STATUS;

export interface BookCancelRefusal {
  code: BookCancelRefusalCode;
  status: number;
}
